import React, { useEffect, useRef, useState } from 'react';
import { XShell } from '../shell.jsx';
import * as workersApi from '../api/workers.js';
import { confirmWorkerFunding, workerOperationError } from './workers-funding.js';
import { WorkersBillingPanel } from '../components/WorkersBillingPanel.jsx';
import { WorkersMonitoringPanel } from '../components/WorkersMonitoringPanel.jsx';
import {
  WorkerList,
  ResourceGrid,
  ResourceInspector,
  WorkerState,
  WorkerIdentifier,
  WorkerPublicUrl,
  workerEnvironmentUrls,
  environmentCanRun,
  environmentState,
  exactMoney,
} from '../components/WorkersWorkspace.jsx';
import { useT } from '../i18n.jsx';
import {
  StatusCodePill,
  MethodPill,
} from './workspace-sections/analytics-shared.jsx';


const Status = ({ value, historical = false }) => {
  const active = value === 'ACTIVE';
  const success = !historical && (active || value === 'SUCCEEDED');
  return (
    <span
      className="mono"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 10,
        color: success ? 'var(--c-mint)' : 'var(--fg-3)',
        letterSpacing: '.08em',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 99,
          background: success ? 'var(--c-mint)' : 'var(--fg-4)',
        }}
      />
      {value}
    </span>
  );
};

const EnvironmentCard = ({ t, environment, onSave, onRetryDomain }) => {
  const [budget, setBudget] = useState(Number(environment.dailyBudgetUsd));
  const [saving, setSaving] = useState(false);
  const dirty = Number(budget) !== Number(environment.dailyBudgetUsd);
  const domain = environment.domains?.[0];
  return (
    <article className="card" style={{ padding: 18, minWidth: 0 }}>
      <div className="row between" style={{ alignItems: 'start' }}>
        <div>
          <div className="micro-mono" style={{ color: 'var(--fg-3)' }}>
            {environment.name}
          </div>
          <div style={{ marginTop: 7 }}>
            <WorkerPublicUrl environment={environment} />
          </div>
        </div>
        <Status value={environmentState(environment)} />
      </div>
      <div style={{ height: 1, background: 'var(--line)', margin: '16px 0' }} />
      <div className="row between" style={{ gap: 12, marginBottom: 14 }}>
        <div>
          <div className="micro">Managed DNS & TLS</div>
          <div className="mono" style={{ marginTop: 5, fontSize: 11 }}>
            {domain?.hostname || environment.subdomain}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Status value={domain?.status || 'NOT_CONFIGURED'} />
          {domain?.status === 'ERROR' && (
            <button
              className="btn btn-sm btn-ghost"
              style={{ display: 'block', marginTop: 6 }}
              onClick={() => onRetryDomain(domain.id)}
            >
              Retry
            </button>
          )}
        </div>
      </div>
      <label className="micro" style={{ display: 'block', marginBottom: 7 }}>
        {t('wk.set.budget')}
      </label>
      <div className="row gap-2">
        <input
          className="input mono"
          type="number"
          min="0.1"
          max="100"
          step="0.1"
          value={budget}
          onChange={(e) => setBudget(e.target.value)}
          style={{ minWidth: 0 }}
        />
        <button
          className="btn btn-sm"
          disabled={!dirty || saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave(environment.name.toLowerCase(), Number(budget));
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </article>
  );
};

const CreatePanel = ({ onCreated, onCancel }) => {
  const [form, setForm] = useState({
    name: '',
    slug: '',
    template: 'worker',
    previewDailyBudgetUsd: 0.25,
    productionDailyBudgetUsd: 2,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (key, value) => setForm((old) => ({ ...old, [key]: value }));
  return (
    <section
      className="card"
      style={{ padding: 24, marginBottom: 22, borderColor: 'var(--line-2)' }}
    >
      <div className="row between">
        <div>
          <div className="micro-mono">NEW WORKER</div>
          <h2 style={{ margin: '5px 0 0', fontSize: 20 }}>
            Create a hosted runtime
          </h2>
        </div>
        <button className="btn btn-sm btn-ghost" onClick={onCancel}>
          Close
        </button>
      </div>
      <div className="workers-form-grid" style={{ marginTop: 20 }}>
        <label>
          <span className="micro">Name</span>
          <input
            className="input"
            value={form.name}
            onChange={(e) => {
              set('name', e.target.value);
              if (!form.slug)
                set(
                  'slug',
                  e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-|-$/g, ''),
                );
            }}
          />
        </label>
        <label>
          <span className="micro">Slug</span>
          <input
            className="input mono"
            value={form.slug}
            onChange={(e) => set('slug', e.target.value)}
          />
        </label>
        <label>
          <span className="micro">Template</span>
          <select
            className="input"
            value={form.template}
            onChange={(e) => set('template', e.target.value)}
          >
            <option value="worker">Worker</option>
            <option value="agent">Agent starter</option>
          </select>
        </label>
        <label>
          <span className="micro">Preview daily budget</span>
          <input
            className="input mono"
            type="number"
            min="0.1"
            max="100"
            value={form.previewDailyBudgetUsd}
            onChange={(e) =>
              set('previewDailyBudgetUsd', Number(e.target.value))
            }
          />
        </label>
        <label>
          <span className="micro">Production daily budget</span>
          <input
            className="input mono"
            type="number"
            min="0.1"
            max="100"
            value={form.productionDailyBudgetUsd}
            onChange={(e) =>
              set('productionDailyBudgetUsd', Number(e.target.value))
            }
          />
        </label>
      </div>
      {error && (
        <div style={{ color: 'var(--err)', marginTop: 12 }}>{error}</div>
      )}
      <div className="row gap-2" style={{ marginTop: 20 }}>
        <button
          className="btn"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError('');
            try {
              onCreated(await workersApi.createWorker(form));
            } catch (e) {
              setError(workerOperationError(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Creating…' : 'Create Worker'}
        </button>
        <span className="micro" style={{ alignSelf: 'center' }}>
          Requires $5 available balance. Deployment and resources require a separate funding confirmation.
        </span>
      </div>
    </section>
  );
};

const starterModule = (template) =>
  template === 'agent'
    ? `export default {
  async fetch(request, env) {
    const url = new URL(request.url);

    if (url.pathname === '/health') {
      return Response.json({ ok: true, runtime: 'xAPI Workers', ai: env.XAPI_AI_BASE_URL });
    }

    if (url.pathname === '/memory' && request.method === 'PUT') {
      if (!env.STATE) return Response.json({ error: 'Create a KV binding named STATE' }, { status: 503 });
      const input = await request.text();
      await env.STATE.put('agent-memory', input);
      return Response.json({ stored: true });
    }

    if (url.pathname === '/memory') {
      if (!env.STATE) return Response.json({ error: 'Create a KV binding named STATE' }, { status: 503 });
      return Response.json({ value: await env.STATE.get('agent-memory') });
    }

    return Response.json({
      message: 'Your persistent Agent endpoint is ready.',
      endpoints: ['GET /health', 'PUT|GET /memory'],
    });
  },
};`
    : `export default {
  async fetch(request, env) {
    const url = new URL(request.url);
    return Response.json({
      ok: true,
      message: 'Hello from xAPI Workers',
      path: url.pathname,
      requestId: request.headers.get('x-xapi-request-id'),
      ai: env.XAPI_AI_BASE_URL,
    });
  },
};`;

const DeployPanel = ({ worker, onChanged }) => {
  const [environment, setEnvironment] = useState('preview');
  const [moduleCode, setModuleCode] = useState(() =>
    starterModule(worker.template),
  );
  const [moduleBundle, setModuleBundle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const target = worker.environments.find(
    (item) => item.name.toLowerCase() === environment,
  );
  const { primaryUrl: publicUrl, accessUrl } = workerEnvironmentUrls(target);
  const invocationUrl = accessUrl || publicUrl || '';

  useEffect(() => {
    setModuleCode(starterModule(worker.template));
    setModuleBundle(null);
    setResult(null);
  }, [worker.id, worker.template]);

  const deploy = async () => {
    if (!moduleBundle && !moduleCode.trim()) {
      setError('Module code is required.');
      return;
    }
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const funding = await confirmWorkerFunding(worker.id, environment, 'worker');
      if (funding === null) return;
      const operation = crypto.randomUUID();
      const artifact = await workersApi.uploadArtifact(
        worker.id,
        moduleBundle || moduleCode,
        `web-upload-${operation}`,
      );
      const deployment = await workersApi.deployWorker(worker.id, {
        environment,
        artifactId: artifact.id,
        ...funding,
        idempotencyKey: `web-deploy-${operation}`,
        compatibilityDate: new Date().toISOString().slice(0, 10),
      });
      setResult({ artifact, deployment });
      await onChanged();
    } catch (e) {
      setError(workerOperationError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" style={{ marginTop: 18, overflow: 'hidden' }}>
      <div
        className="row between"
        style={{ padding: '15px 18px', borderBottom: '1px solid var(--line)' }}
      >
        <div>
          <strong>Quick deploy playground</strong>
          <div className="micro" style={{ marginTop: 4 }}>
            Paste one module, or upload a built module directory as one immutable Artifact.
          </div>
        </div>
        <select
          className="input mono"
          value={environment}
          onChange={(event) => {
            setEnvironment(event.target.value);
            setResult(null);
          }}
          style={{ width: 150 }}
        >
          <option value="preview">PREVIEW</option>
          <option value="production">PRODUCTION</option>
        </select>
      </div>
      <div style={{ padding: 18 }}>
        <div className="row between" style={{ marginBottom: 10 }}>
          <span className="micro-mono">
            {moduleBundle
              ? `${moduleBundle.mainModule} · ${moduleBundle.modules.length} modules`
              : 'src/index.mjs'}
          </span>
          <div className="row gap-2">
          <label className="btn btn-sm btn-ghost" style={{ cursor: 'pointer' }}>
            Load modules
            <input
              type="file"
              accept=".js,.mjs,text/javascript,application/javascript"
              multiple
              hidden
              onChange={async (event) => {
                const files = [...(event.target.files || [])];
                if (files.length) {
                  const modules = await Promise.all(files.map(async file => ({
                    path: file.webkitRelativePath || file.name,
                    content: await file.text(),
                    encoding: 'utf8',
                    contentType: 'application/javascript+module',
                  })));
                  const preferred = ['index.mjs','worker.mjs','_worker.js','index.js'];
                  const mainModule = preferred.map(name => modules.find(module => module.path === name || module.path.endsWith(`/${name}`))).find(Boolean)?.path || modules[0].path;
                  setModuleBundle({ version: 1, mainModule, modules });
                  setError('');
                }
                event.target.value = '';
              }}
            />
          </label>
          <label className="btn btn-sm btn-ghost" style={{ cursor: 'pointer' }}>
            Load build folder
            <input
              type="file"
              accept=".js,.mjs,text/javascript,application/javascript"
              multiple
              webkitdirectory=""
              hidden
              onChange={async (event) => {
                const files = [...(event.target.files || [])].filter(file => /\.(?:m?js)$/i.test(file.name));
                if (files.length) {
                  const modules = await Promise.all(files.map(async file => ({ path: file.webkitRelativePath, content: await file.text(), encoding: 'utf8', contentType: 'application/javascript+module' })));
                  const preferred = ['index.mjs','worker.mjs','_worker.js','index.js'];
                  const mainModule = preferred.map(name => modules.find(module => module.path.endsWith(`/${name}`))).find(Boolean)?.path || modules[0].path;
                  setModuleBundle({ version: 1, mainModule, modules });
                  setError('');
                } else setError('The selected folder has no .js or .mjs build modules.');
                event.target.value = '';
              }}
            />
          </label>
          </div>
        </div>
        {moduleBundle && (
          <div className="workers-bundle-summary">
            <label className="micro-mono" htmlFor="worker-main-module">Entry module</label>
            <select id="worker-main-module" className="input mono" value={moduleBundle.mainModule} onChange={event => setModuleBundle({...moduleBundle, mainModule:event.target.value})}>
              {moduleBundle.modules.map(module => <option key={module.path} value={module.path}>{module.path}</option>)}
            </select>
            <button className="btn btn-sm btn-ghost" onClick={() => setModuleBundle(null)}>Use editor</button>
          </div>
        )}
        <textarea
          className="input mono workers-code-editor"
          aria-label="Worker module code"
          spellCheck="false"
          value={moduleBundle ? `Bundled ${moduleBundle.modules.length} modules. Select “Use editor” to edit a single module.` : moduleCode}
          disabled={Boolean(moduleBundle)}
          onChange={(event) => { setModuleBundle(null); setModuleCode(event.target.value); }}
        />
        <div className="workers-deploy-actions">
          <button className="btn" disabled={busy} onClick={deploy}>
            {busy ? 'Uploading & deploying…' : `Deploy to ${environment}`}
          </button>
          <WorkerPublicUrl environment={target} />
        </div>
        {result && (
          <div className="workers-deploy-result">
            <div>
              <Status value={result.deployment.status} />
              <span className="mono" style={{ marginLeft: 10, fontSize: 11 }}>
                Artifact {result.artifact.contentSha256.slice(0, 12)}
              </span>
            </div>
            <div className="row gap-2">
              <button
                className="btn btn-sm btn-ghost"
                disabled={!invocationUrl}
                onClick={() =>
                  navigator.clipboard.writeText(
                    `curl ${invocationUrl.replace(/\/$/, '')}/health`,
                  )
                }
              >
                Copy curl
              </button>
              <a
                className="btn btn-sm"
                href={invocationUrl || undefined}
                aria-disabled={!invocationUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open Worker
              </a>
            </div>
          </div>
        )}
        {error && (
          <div style={{ color: 'var(--err)', marginTop: 12 }}>{error}</div>
        )}
      </div>
    </section>
  );
};

const SchedulePanel = ({ worker }) => {
  const [items, setItems] = useState([]);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: 'Agent heartbeat',
    environment: 'preview',
    cron: '*/15 * * * *',
    timezone: 'UTC',
    method: 'POST',
    path: '/cron',
  });
  const set = (key, value) => setForm((old) => ({ ...old, [key]: value }));
  const load = async () => {
    try {
      setItems(await workersApi.listSchedules(worker.id));
    } catch (e) {
      setError(workerOperationError(e));
    }
  };
  useEffect(() => {
    setRuns([]);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worker.id]);
  const act = async (name, callback) => {
    setBusy(name);
    setError('');
    try {
      await callback();
      await load();
    } catch (e) {
      setError(workerOperationError(e));
    } finally {
      setBusy('');
    }
  };
  return (
    <section className="card" style={{ marginTop: 18, overflow: 'hidden' }}>
      <div
        className="row between"
        style={{ padding: '15px 18px', borderBottom: '1px solid var(--line)' }}
      >
        <div>
          <strong>Scheduled Agent tasks</strong>
          <div className="micro" style={{ marginTop: 4 }}>
            Persistent five-field Cron · timezone, retries and run history
            included
          </div>
        </div>
        <span className="micro-mono">{items.length} SCHEDULES</span>
      </div>
      <div style={{ padding: 18 }}>
        <div
          className="workers-resource-form"
          style={{ gridTemplateColumns: '1.2fr .7fr 1fr .8fr' }}
        >
          <input
            className="input"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Task name"
          />
          <select
            className="input mono"
            value={form.environment}
            onChange={(e) => set('environment', e.target.value)}
          >
            <option value="preview">PREVIEW</option>
            <option value="production">PRODUCTION</option>
          </select>
          <input
            className="input mono"
            value={form.cron}
            onChange={(e) => set('cron', e.target.value)}
            aria-label="Cron expression"
          />
          <input
            className="input mono"
            value={form.timezone}
            onChange={(e) => set('timezone', e.target.value)}
            placeholder="UTC"
          />
          <select
            className="input mono"
            value={form.method}
            onChange={(e) => set('method', e.target.value)}
          >
            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => (
              <option key={method}>{method}</option>
            ))}
          </select>
          <input
            className="input mono"
            value={form.path}
            onChange={(e) => set('path', e.target.value)}
            placeholder="/cron"
          />
          <button
            className="btn btn-sm"
            disabled={Boolean(busy)}
            onClick={() =>
              act('create', () => workersApi.createSchedule(worker.id, form))
            }
          >
            {busy === 'create' ? 'Creating…' : 'Create schedule'}
          </button>
        </div>
        {items.map((schedule) => (
          <div
            className="workers-resource-row"
            key={schedule.id}
            style={{ marginTop: 10 }}
          >
            <Status value={schedule.enabled ? 'ACTIVE' : 'PAUSED'} />
            <span>
              <strong>{schedule.name}</strong>
              <span className="micro mono" style={{ display: 'block' }}>
                {schedule.cron} · {schedule.timezone}
              </span>
            </span>
            <span className="mono">
              {schedule.method} {schedule.path}
            </span>
            <div className="row gap-2">
              <button
                className="btn btn-sm btn-ghost"
                disabled={Boolean(busy)}
                onClick={() =>
                  act(`run-${schedule.id}`, async () => {
                    await workersApi.runScheduleNow(worker.id, schedule.id);
                    setRuns(
                      await workersApi.listScheduleRuns(worker.id, schedule.id),
                    );
                  })
                }
              >
                {busy === `run-${schedule.id}` ? 'Running…' : 'Run now'}
              </button>
              <button
                className="btn btn-sm btn-ghost"
                disabled={Boolean(busy)}
                onClick={async () =>
                  setRuns(
                    await workersApi.listScheduleRuns(worker.id, schedule.id),
                  )
                }
              >
                Runs
              </button>
              <button
                className="btn btn-sm btn-ghost"
                disabled={Boolean(busy)}
                onClick={() =>
                  act(schedule.id, () =>
                    workersApi.updateSchedule(worker.id, schedule.id, {
                      enabled: !schedule.enabled,
                    }),
                  )
                }
              >
                {schedule.enabled ? 'Pause' : 'Resume'}
              </button>
              <button
                className="btn btn-sm btn-ghost"
                disabled={Boolean(busy)}
                onClick={() =>
                  window.confirm(`Delete ${schedule.name}?`) &&
                  act(schedule.id, () =>
                    workersApi.deleteSchedule(worker.id, schedule.id),
                  )
                }
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {runs.length > 0 && (
          <div style={{ marginTop: 12, borderTop: '1px solid var(--line)' }}>
            {runs.slice(0, 10).map((run) => (
              <div className="workers-invocation-row" key={run.id}>
                <span className="mono">
                  {new Date(run.scheduledAt).toLocaleString()}
                </span>
                <Status value={run.status} />
                <span className="mono">attempt {run.attempt}</span>
                <span className="mono">{run.statusCode || '—'}</span>
                <span className="mono">
                  {run.durationMs == null ? '—' : `${run.durationMs} ms`}
                </span>
              </div>
            ))}
          </div>
        )}
        {error && (
          <div style={{ color: 'var(--err)', marginTop: 12 }}>{error}</div>
        )}
      </div>
    </section>
  );
};

const InvocationPanel = ({
  worker,
  initialEnvironment = 'preview',
  onEnvironmentChange,
}) => {
  const { t } = useT();
  const [environment, setLocalEnvironment] = useState(initialEnvironment);
  const setEnvironment = (value) => {
    setLocalEnvironment(value);
    onEnvironmentChange?.(value);
  };
  const [data, setData] = useState({ items: [], sampled: false });
  const [runtimeLogs, setRuntimeLogs] = useState({ items: [], sampled: false });
  const [view, setView] = useState('calls');
  const [query, setQuery] = useState('');
  const [outcome, setOutcome] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [calls, logs] = await Promise.all([
        workersApi.listInvocations(worker.id, environment),
        workersApi.listRuntimeLogs(worker.id, environment),
      ]);
      setData(calls);
      setRuntimeLogs(logs);
    } catch (e) {
      setError(workerOperationError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worker.id, environment]);

  const normalizedQuery = query.trim().toLowerCase();
  const visibleCalls = (data.items || []).filter((item) => {
    const status = Number(item.status || 0);
    if (outcome === 'success' && status >= 400) return false;
    if (outcome === 'error' && status < 400) return false;
    if (!normalizedQuery) return true;
    return [item.requestId, item.method, item.path, item.status]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedQuery));
  });
  const visibleLogs = (runtimeLogs.items || []).filter((item) => {
    const isError =
      String(item.kind || '').toLowerCase() === 'exception' ||
      String(item.level || '').toLowerCase() === 'error' ||
      String(item.outcome || '').toLowerCase() === 'exception';
    if (outcome === 'success' && isError) return false;
    if (outcome === 'error' && !isError) return false;
    if (!normalizedQuery) return true;
    return [
      item.message,
      item.path,
      item.traceId,
      item.requestId,
      item.kind,
      item.level,
      item.exceptionName,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedQuery));
  });

  return (
    <section className="card" style={{ marginTop: 18, overflow: 'hidden' }}>
      <div
        className="row between"
        style={{ padding: '15px 18px', borderBottom: '1px solid var(--line)' }}
      >
        <div>
          <strong>{t('wk.tab.logs')}</strong>
          <div className="micro" style={{ marginTop: 4 }}>
            {t('wk.l.privacy')}
          </div>
        </div>
        <div className="row gap-2">
          <select
            className="input mono"
            value={environment}
            onChange={(event) => setEnvironment(event.target.value)}
            style={{ width: 145 }}
          >
            <option value="preview">PREVIEW</option>
            <option value="production">PRODUCTION</option>
          </select>
          <button
            className="btn btn-sm btn-ghost"
            disabled={loading}
            onClick={load}
          >
            {loading ? t('wk.refreshing') : t('wk.refresh')}
          </button>
        </div>
      </div>
      <div className="workers-observability-tabs">
        <button
          className={`btn btn-sm ${view === 'calls' ? '' : 'btn-ghost'}`}
          onClick={() => setView('calls')}
        >
          {t('wk.l.calls')}{' '}
          <span className="mono">{data.items?.length || 0}</span>
        </button>
        <button
          className={`btn btn-sm ${view === 'logs' ? '' : 'btn-ghost'}`}
          onClick={() => setView('logs')}
        >
          {t('wk.l.logs')}{' '}
          <span className="mono">{runtimeLogs.items?.length || 0}</span>
        </button>
        <div className="workers-log-filters">
          <input
            className="input mono"
            aria-label="Filter logs and calls"
            placeholder={t('wk.l.filter')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <select
            className="input mono"
            aria-label="Filter outcome"
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
          >
            <option value="all">{t('wk.l.outcome.all')}</option>
            <option value="success">{t('wk.l.outcome.success')}</option>
            <option value="error">{t('wk.l.outcome.error')}</option>
          </select>
        </div>
      </div>
      {error ? (
        <div style={{ padding: 18, color: 'var(--err)' }}>{error}</div>
      ) : view === 'logs' && visibleLogs.length ? (
        <div className="workers-runtime-logs">
          {visibleLogs.map((item, index) => (
            <div
              className={`workers-runtime-log workers-runtime-log-${String(item.kind || '').toLowerCase()}`}
              key={`${item.timestamp}-${item.kind}-${index}`}
            >
              <span className="mono" style={{ color: 'var(--fg-3)' }}>
                {new Date(item.timestamp).toLocaleString()}
              </span>
              <Status value={item.kind || 'LOG'} />
              <span className="micro-mono">{item.level || item.outcome}</span>
              <div className="workers-runtime-log-message">
                <code>{item.message || item.path || item.outcome || '—'}</code>
                {(item.exceptionName || item.traceId || item.requestId) && (
                  <div className="micro" style={{ marginTop: 5 }}>
                    {item.exceptionName ? `${item.exceptionName} · ` : ''}
                    trace {item.traceId || item.requestId}
                  </div>
                )}
              </div>
            </div>
          ))}
          {runtimeLogs.sampled && (
            <div className="micro" style={{ padding: '10px 18px' }}>
              {t('wk.l.sampled')}
            </div>
          )}
        </div>
      ) : view === 'calls' && visibleCalls.length ? (
        <div className="workers-invocations">
          {visibleCalls.map((item, index) => (
            <div
              className="workers-invocation-row"
              key={`${item.requestId}-${index}`}
            >
              <span className="mono" style={{ color: 'var(--fg-3)' }}>
                {new Date(item.timestamp).toLocaleString()}
              </span>
              <MethodPill method={item.method} />
              <span className="mono" style={{ overflowWrap: 'anywhere' }}>
                {item.path}
              </span>
              <StatusCodePill code={Number(item.status)} />
              <span className="mono">
                {Math.round(Number(item.durationMs))} ms
              </span>
            </div>
          ))}
          {data.sampled && (
            <div className="micro" style={{ padding: '10px 18px' }}>
              {t('wk.l.sampled')}
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: 20, color: 'var(--fg-3)' }}>
          {loading
            ? t('wk.refreshing')
            : normalizedQuery || outcome !== 'all'
              ? t('wk.l.noMatch')
              : t('wk.l.empty', {
                  view: view === 'calls' ? t('wk.l.calls') : t('wk.l.logs'),
                })}
        </div>
      )}
    </section>
  );
};

const r2CanRetry = (resource) =>
  resource?.type === 'R2_BUCKET' &&
  ['PROVISIONING', 'ERROR'].includes(resource.status) &&
  !resource.config?.__xapiDeletionIntentV1 &&
  !resource.config?.controlDeletionRequested;
const r2AwaitingEvents = (resource) =>
  r2CanRetry(resource) &&
  resource.status === 'PROVISIONING' &&
  resource.providerResourceId &&
  resource.errorCode === 'R2_EVENT_CAPTURE_NOT_READY';
export const ResourcePanel = ({
  worker,
  capabilities,
  onChanged,
  environment,
  setEnvironment,
  onLogs,
  openResourceId,
  onOpenResourceHandled,
}) => {
  const { t } = useT();
  const [resourceId, setResourceId] = useState(openResourceId || null);
  useEffect(() => setResourceId(null), [worker.id, environment]);
  useEffect(() => {
    if (!openResourceId) return;
    setResourceId(openResourceId);
    onOpenResourceHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openResourceId]);
  const [resourceType, setResourceType] = useState('kv_namespace');
  const [bindingName, setBindingName] = useState('STATE');
  const [className, setClassName] = useState('AgentState');
  const [secretName, setSecretName] = useState('MODEL_KEY');
  const [secretValue, setSecretValue] = useState('');
  const [secrets, setSecrets] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const r2Operation = useRef(0);
  const r2NoticeBinding = useRef(null);
  useEffect(() => {
    setNotice(''); setError('');
    setBusy((previous) => previous === 'r2-resource' ? '' : previous);
    const operation = ++r2Operation.current;
    // A request in the previous view may still complete on the server. Refresh
    // on return without holding a cross-environment client lock.
    if (r2NoticeBinding.current) {
      Promise.resolve().then(() => onChanged()).then((refreshed) => {
        if (refreshed === false) throw new Error('resource refresh failed');
      }).catch(() => {
        if (operation === r2Operation.current) setError(t('wk.r.r2RefreshFailed'));
      });
    }
    return () => { r2Operation.current += 1; };
    // Refresh only on navigation, not every new callback identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worker.id, environment]);
  const selectedEnvironment = worker.environments.find(
    (item) => item.name.toLowerCase() === environment,
  );
  const resources = selectedEnvironment?.resources || [];
  useEffect(() => {
    const resource = resources.find((item) => item.bindingName === r2NoticeBinding.current);
    if (notice && resource && resource.status !== 'PROVISIONING') setNotice('');
  }, [resources, notice]);
  const capabilityMap = Object.fromEntries(
    (capabilities?.items || []).map((item) => [item.type, item]),
  );
  const selectedCapability = capabilityMap[resourceType];

  const loadSecrets = async () => {
    setError('');
    try {
      setSecrets(await workersApi.listSecrets(worker.id, environment));
    } catch (e) {
      setError(workerOperationError(e));
    }
  };

  useEffect(() => {
    loadSecrets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worker.id, environment]);

  const mutate = async (name, action) => {
    setBusy(name);
    setError('');
    try {
      await action();
      await Promise.all([onChanged(), loadSecrets()]);
    } catch (e) {
      setError(workerOperationError(e));
    } finally {
      setBusy('');
    }
  };

  // Explicit user retry of the same binding; never poll or recreate in the UI.
  const createR2 = async (name) => {
    r2NoticeBinding.current = name;
    const operation = ++r2Operation.current;
    const current = () => operation === r2Operation.current;
    setBusy('r2-resource');
    setError('');
    setNotice('');
    const existing = resources.find((item) => item.bindingName === name);
    try {
      const funding = r2CanRetry(existing) && existing.status === 'PROVISIONING' && existing.providerResourceId
        ? {}
        : await confirmWorkerFunding(worker.id, environment, 'r2_bucket');
      if (funding === null || !current()) return;
      const result = await workersApi.createResource(worker.id, environment, {
        type: 'r2_bucket',
        bindingName: name,
        ...funding,
      });
      if (!current()) return;
      if (result?.initialization?.status === 'PENDING') {
        setNotice(
          t(
            result.initialization.automaticProbeRetry === false
              ? 'wk.r.r2Review'
              : 'wk.r.r2Pending',
          ),
        );
      } else if (result?.status === 'PROVISIONING') {
        setNotice(t('wk.r.r2Pending'));
      }
    } catch (e) {
      if (!current()) return;
      if (e.data?.code === 'R2_EVENT_CAPTURE_NOT_READY') {
        setNotice(
          t(
            e.data.automaticProbeRetry === false
              ? 'wk.r.r2Review'
              : 'wk.r.r2Pending',
          ),
        );
      } else {
        setError(workerOperationError(e));
      }
    } finally {
      // Even an interrupted response may have created the bucket. Read back
      // state so the next user attempt uses the same binding.
      if (current()) {
        try {
          const refreshed = await onChanged();
          if (refreshed === false) throw new Error('resource refresh failed');
        } catch {
          if (current()) setError((previous) =>
            [previous, t('wk.r.r2RefreshFailed')].filter(Boolean).join(' '));
        }
        if (current()) setBusy('');
      }
    }
  };
  const retryR2Button = (resource) =>
    r2CanRetry(resource) && (
      <button
        className="btn btn-sm btn-ghost"
        disabled={Boolean(busy) || capabilityMap.r2_bucket?.available === false}
        onClick={() => createR2(resource.bindingName)}
      >
        {t(r2AwaitingEvents(resource) ? 'wk.r.r2Continue' : 'wk.r.r2Retry')}
      </button>
    );
  const selectedResource = resources.find(
    (resource) => resource.id === resourceId,
  );
  const removeResource = (resource) => {
    if (
      !window.confirm(
        `Delete ${resource.bindingName}? This deletes the managed resource and may permanently remove its data.`,
      )
    )
      return;
    mutate(`resource-${resource.id}`, () =>
      workersApi.deleteResource(worker.id, environment, resource.id),
    );
  };
  if (selectedResource)
    return (
      <ResourceInspector
        key={`${environment}:${resourceId}`}
        worker={worker}
        environment={environment}
        resource={selectedResource}
        onBack={() => setResourceId(null)}
        onLogs={onLogs}
      >
        <div className="ww-res-inspector-actions">
          {retryR2Button(selectedResource)}
          <button
            className="btn btn-sm btn-ghost"
            disabled={Boolean(busy)}
            onClick={() => removeResource(selectedResource)}
          >
            {t('wk.r.delete')}
          </button>
        </div>
        {notice && <div role="status" className="micro" style={{ padding: 18 }}>{notice}</div>}
        {error && (
          <div role="alert" className="workers-inline-error">
            {error}
          </div>
        )}
      </ResourceInspector>
    );

  return (
    <section className="card" style={{ marginTop: 18, overflow: 'hidden' }}>
      <div
        className="row between"
        style={{ padding: '15px 18px', borderBottom: '1px solid var(--line)' }}
      >
        <div>
          <strong>Cloudflare resources</strong>
          <div className="micro" style={{ marginTop: 4 }}>
            Isolated bindings managed by xAPI · redeploy after resource changes
          </div>
        </div>
        <select
          className="input mono"
          value={environment}
          onChange={(event) => setEnvironment(event.target.value)}
          style={{ width: 150 }}
        >
          <option value="preview">PREVIEW</option>
          <option value="production">PRODUCTION</option>
        </select>
      </div>

      <div style={{ padding: 18 }}>
        {notice && <div role="status" className="micro" style={{ marginBottom: 12 }}>{notice}</div>}
        <ResourceGrid
          t={t}
          worker={worker}
          environment={environment}
          resources={resources}
          onSelect={setResourceId}
          renderActions={(resource) => (
            <>
              {retryR2Button(resource)}
              <button className="btn btn-sm btn-ghost" onClick={onLogs}>
                {t('wk.r.logs')} →
              </button>
              <button
                className="btn btn-sm btn-ghost"
                disabled={Boolean(busy)}
                onClick={() => removeResource(resource)}
              >
                {t('wk.r.delete')}
              </button>
            </>
          )}
        />
        <details className="ww-resource-management">
          <summary>Add a resource</summary>
          <div className="workers-resource-form">
            <select
              className="input"
              value={resourceType}
              onChange={(event) => setResourceType(event.target.value)}
            >
              <option
                value="kv_namespace"
                disabled={capabilityMap.kv_namespace?.available === false}
              >
                KV namespace
              </option>
              <option
                value="d1_database"
                disabled={capabilityMap.d1_database?.available === false}
              >
                D1 database
              </option>
              <option
                value="r2_bucket"
                disabled={capabilityMap.r2_bucket?.available === false}
              >
                R2 bucket
              </option>
              <option
                value="durable_object"
                disabled={capabilityMap.durable_object?.available === false}
              >
                Durable Object
              </option>
              <option
                value="queue"
                disabled={capabilityMap.queue?.available === false}
              >
                Queue
              </option>
              <option
                value="workflow"
                disabled={capabilityMap.workflow?.available === false}
              >
                Workflow
              </option>
            </select>
            <input
              className="input mono"
              value={bindingName}
              onChange={(event) =>
                setBindingName(event.target.value.toUpperCase())
              }
              placeholder="STATE"
            />
            {resourceType === 'durable_object' && (
              <input
                className="input mono"
                value={className}
                onChange={(event) => setClassName(event.target.value)}
                placeholder="Exported class name"
                aria-label="Exported class name"
              />
            )}
            <button
              className="btn btn-sm"
              disabled={
                Boolean(busy) || selectedCapability?.available === false
              }
              onClick={() => resourceType === 'r2_bucket' ? createR2(bindingName) :
                mutate('resource', async () => {
                  const funding = await confirmWorkerFunding(worker.id, environment, resourceType);
                  if (funding === null) return;
                  await workersApi.createResource(worker.id, environment, {
                    type: resourceType,
                    bindingName,
                    ...funding,
                    ...(resourceType === 'durable_object' ? { className } : {}),
                  });
                })
              }
            >
              {['resource', 'r2-resource'].includes(busy) ? 'Creating…' : 'Create resource'}
            </button>
          </div>

          <div className="micro" style={{ marginTop: 9, color: 'var(--fg-3)' }}>
            {t(`wk.resUse.${resourceType}`)}
            {resourceType === 'queue' && (
              <>
                {' '}
                Messages use <code>{'{ path, method, body }'}</code>; omitted
                paths are sent to <code>/__xapi/queue</code>.
              </>
            )}
          </div>

          {selectedCapability?.available === false && (
            <div className="workers-capability-warning">
              <strong>{selectedCapability.permission} is required.</strong>{' '}
              {selectedCapability.reason ||
                'The configured Cloudflare token cannot create this resource.'}
            </div>
          )}

          {capabilities?.items?.length > 0 && (
            <div className="workers-capability-grid">
              {capabilities.items.map((item) => (
                <div key={item.type}>
                  <Status
                    value={item.available ? 'AVAILABLE' : 'UNAVAILABLE'}
                  />
                  <span className="micro-mono">{item.type}</span>
                  <span
                    className="micro"
                    title={item.reason || item.note || ''}
                  >
                    {item.available
                      ? item.permission
                      : `${item.permission}: ${item.reason || 'permission check failed'}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </details>
        <details className="ww-resource-management">
          <summary>Encrypted Secrets · configure bindings</summary>
          <div className="row between" style={{ marginBottom: 10 }}>
            <strong style={{ fontSize: 13 }}>Encrypted Secrets</strong>
            <span className="micro">Values are write-only</span>
          </div>
          <div className="workers-secret-form">
            <input
              className="input mono"
              value={secretName}
              onChange={(event) =>
                setSecretName(event.target.value.toUpperCase())
              }
              placeholder="MODEL_KEY"
            />
            <input
              className="input mono"
              type="password"
              autoComplete="new-password"
              value={secretValue}
              onChange={(event) => setSecretValue(event.target.value)}
              placeholder="Secret value"
            />
            <button
              className="btn btn-sm"
              disabled={Boolean(busy) || !secretValue}
              onClick={() =>
                mutate('secret', async () => {
                  await workersApi.putSecret(
                    worker.id,
                    environment,
                    secretName,
                    secretValue,
                  );
                  setSecretValue('');
                })
              }
            >
              {busy === 'secret' ? 'Saving…' : 'Save / rotate'}
            </button>
          </div>
          {secrets.map((secret) => (
            <div className="workers-resource-row" key={secret.id}>
              <Status value={secret.deployedAt ? 'ACTIVE' : 'PENDING'} />
              <span className="mono">env.{secret.bindingName}</span>
              <span className="micro-mono">VERSION {secret.version}</span>
              <button
                className="btn btn-sm btn-ghost"
                disabled={Boolean(busy)}
                onClick={() => {
                  if (!window.confirm(`Delete secret ${secret.bindingName}?`))
                    return;
                  mutate(`secret-${secret.id}`, () =>
                    workersApi.deleteSecret(
                      worker.id,
                      environment,
                      secret.bindingName,
                    ),
                  );
                }}
              >
                Delete
              </button>
            </div>
          ))}
        </details>
        {error && (
          <div style={{ color: 'var(--err)', marginTop: 12 }}>{error}</div>
        )}
      </div>
    </section>
  );
};

const ProjectWorkflow = ({ worker }) => (
  <section className="card workers-project-workflow">
    <div className="workers-panel-head row between">
      <div>
        <div className="micro-mono">RECOMMENDED RELEASE PATH</div>
        <strong>Deploy a Worker project with the xAPI CLI</strong>
      </div>
      <span className="workers-workflow-badge">
        LOCAL → PREVIEW → PRODUCTION
      </span>
    </div>
    <div className="workers-workflow-grid">
      <div>
        <span className="workers-step">01</span>
        <strong>Initialize or import</strong>
        <p>
          Start a new Agent/Worker project, or import an existing Cloudflare
          Wrangler configuration.
        </p>
      </div>
      <div>
        <span className="workers-step">02</span>
        <strong>Review the plan</strong>
        <p>
          See code, resources, secrets metadata and environment changes before
          anything is uploaded.
        </p>
      </div>
      <div>
        <span className="workers-step">03</span>
        <strong>Push preview</strong>
        <p>
          Build an immutable Artifact and publish it to the isolated preview
          environment.
        </p>
      </div>
      <div>
        <span className="workers-step">04</span>
        <strong>Promote exactly</strong>
        <p>Promote the tested Artifact to production without rebuilding it.</p>
      </div>
    </div>
    <pre className="code workers-project-command">{`# New project
xapi workers init my-agent --template agent

# Existing Cloudflare Worker
xapi workers init --from-wrangler ./wrangler.jsonc

cd my-agent
xapi workers plan --env preview
xapi workers push --env preview
xapi workers promote --to production

# Recover code without changing data or secrets
xapi workers rollback --env production --to previous`}</pre>
    <div className="workers-workflow-note">
      <strong>Project binding</strong>
      <span>
        {worker
          ? `This console project is ${worker.name} (${worker.id}). Keep the generated xapi.worker.json in source control; keep secret values out of Git.`
          : 'The generated xapi.worker.json binds local configuration to the hosted Worker. Secret values remain outside source control.'}
      </span>
    </div>
  </section>
);

export const ReleasePanel = ({ worker, buildProvider, onChanged, environment: selectedName, setEnvironment }) => {
  const [rollbackBusy, setRollbackBusy] = useState('');
  const [rollbackError, setRollbackError] = useState('');
  const [rollbackResult, setRollbackResult] = useState('');
  const deployments = worker.deployments || [];
  const artifacts = worker.artifacts || [];
  const builds = worker.builds || [];
  const selectedEnvironment = worker.environments.find(
    (environment) => environment.name.toLowerCase() === selectedName,
  );
  // The API returns the latest 20 project-wide records, not 20 per environment.
  const visibleDeployments = selectedEnvironment
    ? deployments.filter((deployment) => deployment.environmentId === selectedEnvironment.id)
    : [];

  const rollback = async (deployment) => {
    const environment = selectedEnvironment;
    if (!environmentCanRun(environment) || deployment.environmentId !== environment?.id ||
        deployment.status !== 'ACTIVE' || environment.activeDeploymentId === deployment.id) {
      setRollbackError('Rollback unavailable for this environment or deployment.');
      return;
    }
    const warning = `Roll back ${environment.name.toLowerCase()} code to deployment ${deployment.id}?\n\nOnly Worker code and compatibility settings are restored. KV, D1, R2, Durable Objects, Queues, Workflows and Secrets are NOT rolled back.`;
    if (!window.confirm(warning)) return;
    setRollbackBusy(deployment.id);
    setRollbackError('');
    setRollbackResult('');
    try {
      const active = environment.activeDeploymentId;
      const idempotencyKey = `console-rb:${active}:${deployment.id}`;
      await workersApi.rollbackWorker(
        worker.id,
        environment.name.toLowerCase(),
        deployment.id,
        idempotencyKey,
      );
      setRollbackResult(
        `${environment.name} code now points to the selected Artifact. Data and secrets were unchanged.`,
      );
      await onChanged();
    } catch (error) {
      setRollbackError(error.message);
    } finally {
      setRollbackBusy('');
    }
  };

  return (
    <>
      <section className="card workers-list-card ww-release-history">
        <div className="workers-panel-head row between">
          <div>
            <strong>Deployment history</strong>
            <p className="micro">Filtered from the latest 20 project-wide deployments.</p>
          </div>
          <label>
            <span className="sr-only">Deployment history environment</span>
            <select className="input mono" value={selectedName} disabled={Boolean(rollbackBusy)}
              onChange={(event) => { setEnvironment(event.target.value); setRollbackError(''); setRollbackResult(''); }}>
              {worker.environments.map((env) => <option key={env.id} value={env.name.toLowerCase()}>{env.name}</option>)}
            </select>
          </label>
          <WorkerState value={environmentState(selectedEnvironment)} />
        </div>
        {rollbackError && (
          <div className="workers-inline-error" role="alert">{rollbackError}</div>
        )}
        {rollbackResult && (
          <div className="workers-inline-success" role="status">{rollbackResult}</div>
        )}
        {visibleDeployments.length === 0 ? (
          <div className="workers-empty-state">
            <strong>No {selectedName} deployments in the loaded history.</strong>
          </div>
        ) : (
          visibleDeployments.map((deployment) => {
            const environment = selectedEnvironment;
            const isCurrent = environment.activeDeploymentId === deployment.id;
            const isLiveCurrent = isCurrent && environmentCanRun(environment);
            const canRollback =
              environmentCanRun(environment) && deployment.status === 'ACTIVE' && !isCurrent;
            return (
              <div
                key={deployment.id}
                className="workers-deployment-row workers-release-row"
              >
                <div>
                  <span className="micro">Result · </span><Status value={deployment.status} historical={!isLiveCurrent} />
                  <div className="micro-mono" style={{ marginTop: 5 }}>
                    {environment.name}{' '}
                    {isCurrent ? (isLiveCurrent ? '· CURRENT' : '· LAST DEPLOYMENT') : ''}
                  </div>
                </div>
                <div>
                  <div className="mono" style={{ fontSize: 11 }}>
                    {(
                      deployment.sourceSha256 ||
                      deployment.artifactId ||
                      ''
                    ).slice(0, 12)}
                  </div>
                  <details className="ww-help">
                    <summary>Deployment details</summary>
                    <code>{deployment.id}</code>
                    <p>Actor: {deployment.actor?.label || 'Legacy record unavailable'}</p>
                    <p>Compatibility: {deployment.compatibilityDate || '—'}</p>
                  </details>
                </div>
                <span
                  className="mono"
                  style={{ fontSize: 11, color: 'var(--fg-4)' }}
                >
                  {deployment.artifactId ? 'Artifact' : 'Legacy deployment'}
                </span>
                <span
                  className="mono"
                  style={{ fontSize: 11, color: 'var(--fg-4)' }}
                >
                  {new Date(deployment.createdAt).toLocaleString()}
                </span>
                <button
                  className="btn btn-sm btn-ghost"
                  disabled={!canRollback || Boolean(rollbackBusy)}
                  title={
                    !environmentCanRun(environment)
                      ? `Environment ${environmentState(environment)}: rollback unavailable`
                      : isCurrent
                      ? 'This deployment is already current'
                      : canRollback
                        ? 'Restore this code Artifact; data and secrets stay unchanged'
                        : 'Only successful historical deployments can be restored'
                  }
                  onClick={() => rollback(deployment)}
                >
                  {rollbackBusy === deployment.id
                    ? 'Rolling back…'
                    : isCurrent
                      ? (isLiveCurrent ? 'Current' : 'Last deployment')
                      : 'Roll back code'}
                </button>
              </div>
            );
          })
        )}
      </section>
      <details className="card ww-help ww-release-extra">
        <summary>CLI deployment guide</summary>
        <ProjectWorkflow worker={worker} />
      </details>
      <details className="card ww-help ww-release-extra">
        <summary>Quick deploy</summary>
        <DeployPanel worker={worker} onChanged={onChanged} />
      </details>
      <details className="card ww-help ww-release-extra">
        <summary>Artifacts</summary>
      <section className="card workers-list-card">
        <div className="workers-panel-head row between">
          <strong>Artifacts</strong>
          <span className="micro">
            Immutable deployable modules · latest 20
          </span>
        </div>
        {artifacts.length === 0 ? (
          <div className="workers-empty-state">
            <strong>No Artifact yet</strong>
            <span>
              Build locally or in CI, then run xapi workers push --env preview.
            </span>
          </div>
        ) : (
          artifacts.map((artifact) => (
            <div key={artifact.id} className="workers-deployment-row">
              <span className="micro-mono">{artifact.source}</span>
              <span className="mono" style={{ fontSize: 11 }}>
                {artifact.contentSha256.slice(0, 12)}
              </span>
              <span
                className="mono"
                style={{ fontSize: 11, color: 'var(--fg-4)' }}
              >
                {artifact.sizeBytes} B
              </span>
              <span
                className="mono"
                style={{ fontSize: 11, color: 'var(--fg-4)' }}
              >
                {new Date(artifact.createdAt).toLocaleString()}
              </span>
            </div>
          ))
        )}
      </section>
      </details>
      <details className="card ww-help ww-release-extra">
        <summary>Builds</summary>
      <section className="card workers-list-card">
        <div className="workers-panel-head row between">
          <strong>Optional Sandbox builds</strong>
          <span className="micro">
            {buildProvider?.sandbox?.configured
              ? 'Available'
              : 'Not configured'}{' '}
            · latest 20
          </span>
        </div>
        {builds.length === 0 ? (
          <div className="workers-empty-state">
            <strong>No Sandbox build</strong>
            <span>
              This provider is optional; locally built Artifacts deploy
              directly.
            </span>
          </div>
        ) : (
          builds.map((build) => (
            <div key={build.id} className="workers-deployment-row">
              <Status value={build.status} />
              <span className="mono" style={{ fontSize: 11 }}>
                {(build.artifactId || build.sourceSha256).slice(0, 12)}
              </span>
              <span
                className="mono"
                style={{ fontSize: 11, color: 'var(--fg-4)' }}
              >
                {build.artifactId ? 'Artifact ready' : 'No artifact'}
              </span>
              <span
                className="mono"
                style={{ fontSize: 11, color: 'var(--fg-4)' }}
              >
                {new Date(build.createdAt).toLocaleString()}
              </span>
            </div>
          ))
        )}
      </section>
      </details>
    </>
  );
};

export const WorkersPage = ({ embedded = false }) => {
  const { t } = useT();
  const { route, navigate } = XShell.useRoute();
  const { authed, user } = XShell.useAuth();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [provider, setProvider] = useState(null);
  const [artifactProvider, setArtifactProvider] = useState(null);
  const [buildProvider, setBuildProvider] = useState(null);
  const [billing, setBilling] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [detailTab, setDetailTab] = useState('health');
  const [openResourceId, setOpenResourceId] = useState(null);
  const [environment, setEnvironment] = useState('preview');
  const reloadVersion = useRef(0);
  const selectedId = embedded ? route.params.worker : route.params.id;
  const navigateToWorker = (workerId) => {
    if (embedded) {
      navigate('console', {
        tab: 'workers',
        ...(workerId ? { worker: workerId } : {}),
      });
      return;
    }
    navigate('workers', workerId ? { id: workerId } : {});
  };

  const reload = async (id = selectedId) => {
    const version = ++reloadVersion.current;
    setError('');
    // Attach rejection handlers immediately. The critical Worker requests below
    // can take longer than an unavailable optional provider endpoint, and waiting
    // to call allSettled would otherwise surface a transient unhandled rejection.
    const optionalStatusResults = Promise.allSettled([
      workersApi.getProviderStatus(),
      workersApi.getArtifactProviderStatus(),
      workersApi.getBuildProviderStatus(),
      workersApi.getBillingStatus(),
      workersApi.getProviderCapabilities(),
    ]);
    try {
      const list = await workersApi.listWorkers({ includeDeleted: true });
      if (version !== reloadVersion.current) return;
      setItems(Array.isArray(list) ? list : []);
      if (id) {
        const detail = await workersApi.getWorker(id);
        if (version !== reloadVersion.current) return;
        setSelected(detail);
      } else setSelected(null);
    } catch (e) {
      if (version === reloadVersion.current) setError(workerOperationError(e));
      return false;
    } finally {
      if (version === reloadVersion.current) setLoading(false);
    }

    const [
      providerResult,
      artifactResult,
      buildResult,
      billingResult,
      capabilitiesResult,
    ] = await optionalStatusResults;
    if (version !== reloadVersion.current) return;
    setProvider(
      providerResult.status === 'fulfilled' ? providerResult.value : null,
    );
    setArtifactProvider(
      artifactResult.status === 'fulfilled' ? artifactResult.value : null,
    );
    setBuildProvider(
      buildResult.status === 'fulfilled' ? buildResult.value : null,
    );
    setBilling(
      billingResult.status === 'fulfilled' ? billingResult.value : null,
    );
    setCapabilities(
      capabilitiesResult.status === 'fulfilled'
        ? capabilitiesResult.value
        : null,
    );
  };

  useEffect(() => {
    if (!authed) {
      reloadVersion.current += 1;
      setSelected(null);
      setItems([]);
      setLoading(false);
      return;
    }
    reload();
    return () => {
      reloadVersion.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, selectedId]);

  useEffect(() => {
    setDetailTab('health');
    setSelected(null);
  }, [selectedId]);

  if (!authed)
    return (
      <main
        style={{
          maxWidth: 780,
          margin: '0 auto',
          padding: '110px 28px',
          textAlign: 'center',
        }}
      >
        <div className="micro-mono">XAPI WORKERS</div>
        <h1 style={{ fontSize: 38, margin: '12px 0' }}>
          Deploy user code on a managed edge runtime.
        </h1>
        <p style={{ color: 'var(--fg-3)' }}>
          Sign in to create Workers, configure isolated environments, and
          inspect deployments.
        </p>
        <button
          className="btn"
          onClick={() => navigate('login')}
          style={{ marginTop: 18 }}
        >
          Sign in
        </button>
      </main>
    );

  return (
    <main
      className={`ww-workspace ${embedded ? 'workers-console-tab' : ''}`}
      style={
        embedded
          ? { minWidth: 0 }
          : { maxWidth: 1240, margin: '0 auto', padding: '38px 28px 100px' }
      }
    >
      {!selectedId && (
        <header className="ww-toolbar" style={{ marginBottom: 28 }}>
          <div>
            <div className="micro-mono">COMPUTE</div>
            <h1 style={{ margin: '8px 0', fontSize: 32 }}>Workers</h1>
            <p className="ww-muted">
              Your applications, environments and connected resources.
            </p>
          </div>
          <button className="btn" onClick={() => setCreating(true)}>
            New Worker
          </button>
        </header>
      )}
      {creating && (
        <CreatePanel
          onCancel={() => setCreating(false)}
          onCreated={(worker) => {
            setCreating(false);
            navigateToWorker(worker.id);
          }}
        />
      )}
      {error && (
        <div role="alert" className="workers-inline-error">
          {error}
          <button className="btn btn-sm" onClick={() => reload()}>
            Retry
          </button>
        </div>
      )}
      {!selectedId ? (
        <>
          <WorkerList
            items={items}
            loading={loading}
            onSelect={navigateToWorker}
          />
          <details
            className="card ww-getting-started"
            style={{ marginTop: 24 }}
          >
            <summary>使用教程</summary>
            <h3 style={{ marginTop: 20 }}>部署前检查</h3>
            <p>
              创建 Worker 需要至少 $5 可用余额。运行环境、代码包存储和预算保护由
              xAPI 平台维护，无需自行配置 Cloudflare 账户。
            </p>
            <dl className="ww-facts">
              {[
                ['Available balance', exactMoney(user.balance)],
                [
                  'Cloudflare runtime',
                  provider
                    ? provider.configured
                      ? 'Configured'
                      : 'Platform setup pending'
                    : 'Status unavailable',
                ],
                [
                  'Artifact store',
                  artifactProvider
                    ? artifactProvider.configured
                      ? 'Configured'
                      : 'Platform setup pending'
                    : 'Status unavailable',
                ],
                [
                  'Billing enforcement',
                  billing
                    ? billing.guarantees?.dailyBudgetHardLimit
                      ? 'Reported enabled'
                      : 'Hard-limit status unconfirmed'
                    : 'Status unavailable',
                ],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            <p>
              平台状态仅反映配置与接口报告，不代表部署、计费、暂停恢复或删除流程已通过业务验收。
              预算硬限制状态未确认时，请联系平台支持；这不表示计费未启用。
            </p>
            <ProjectWorkflow />
          </details>
        </>
      ) : (
        <>
          <div className="ww-breadcrumb">
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => navigateToWorker(null)}
            >
              ← All Workers
            </button>
            <span>/</span>
            <span>{selected?.name || 'Worker'}</span>
          </div>
          {!selected ? (
            <div className="card ww-empty" role="status">
              {error
                ? 'Worker details could not be loaded.'
                : 'Loading Worker…'}
            </div>
          ) : (
            <>
              <header className="ww-toolbar ww-detail-header">
                <div className="ww-detail-identity">
                  <div className="ww-detail-title">
                    <h2 title="创建 Worker 时设置的显示名称；通过 CLI 创建时来自 xapi.worker.json 的 worker.name。">
                      {selected.name}
                    </h2>
                    <WorkerState value={selected.status} />
                  </div>
                  <div className="ww-detail-meta">
                    <WorkerPublicUrl
                      environment={selected.environments.find(
                        (item) => item.name.toLowerCase() === environment,
                      )}
                    />
                    <WorkerIdentifier key={selected.id} id={selected.id} />
                  </div>
                </div>
                <div className="ww-controls">
                  <button
                    className="btn btn-sm btn-ghost"
                    aria-label="Refresh Worker"
                    title="Refresh Worker"
                    onClick={() => reload(selected.id)}
                  >
                    <span aria-hidden="true">↻</span>
                  </button>
                </div>
              </header>
              {selected.status === 'DELETED' ? (
                <>
                  <section className="card" role="note" style={{ padding: 20, marginBottom: 20 }}>
                    <h3>已删除项目 · 历史账单与清理记录</h3>
                    <p>云资源已删除，不能恢复运行或重新部署到此项目。账单、资金变动与删除回执继续保留供核对；未完成的尾账和解冻以实际记录为准。</p>
                  </section>
                  <WorkersBillingPanel key={`${selected.id}:${environment}`} worker={selected} initialEnvironment={environment} onEnvironmentChange={setEnvironment} />
                </>
              ) : <><nav
                className="workers-detail-tabs"
                role="tablist"
                aria-label="Worker sections"
              >
                {[
                  ['health', t('wk.tab.health')],
                  ['resources', t('wk.tab.resources')],
                  ['spend', t('wk.tab.spend')],
                  ['logs', t('wk.tab.logs')],
                  ['settings', t('wk.tab.settings')],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={detailTab === value}
                    className={`${detailTab === value ? 'is-active' : ''}${value === 'settings' ? ' is-secondary' : ''}`}
                    onClick={() => setDetailTab(value)}
                  >
                    {label}
                  </button>
                ))}
              </nav>
              <div role="tabpanel" className="workers-detail-panel">
                {detailTab === 'health' && (
                  <WorkersMonitoringPanel
                    key={`${selected.id}:${environment}`}
                    worker={selected}
                    initialEnvironmentValue={environment}
                    onEnvironmentChange={setEnvironment}
                    onOpenResource={(id) => {
                      setOpenResourceId(id);
                      setDetailTab('resources');
                    }}
                  />
                )}
                {detailTab === 'resources' && (
                  <ResourcePanel
                    openResourceId={openResourceId}
                    onOpenResourceHandled={() => setOpenResourceId(null)}
                    worker={selected}
                    environment={environment}
                    setEnvironment={setEnvironment}
                    onLogs={() => setDetailTab('logs')}
                    capabilities={capabilities}
                    onChanged={async () => reload(selected.id)}
                  />
                )}
                {detailTab === 'spend' && (
                  <WorkersBillingPanel
                    key={`${selected.id}:${environment}`}
                    worker={selected}
                    initialEnvironment={environment}
                    onEnvironmentChange={setEnvironment}
                  />
                )}
                {detailTab === 'logs' && (
                  <InvocationPanel
                    key={`${selected.id}:${environment}`}
                    worker={selected}
                    initialEnvironment={environment}
                    onEnvironmentChange={setEnvironment}
                  />
                )}
                {detailTab === 'settings' && (
                  <div className="workers-settings">
                    <details className="ww-settings-section" open>
                      <summary>{t('wk.set.deploy')}</summary>
                      <ReleasePanel
                        worker={selected}
                        buildProvider={buildProvider}
                        environment={environment}
                        setEnvironment={setEnvironment}
                        onChanged={async () => reload(selected.id)}
                      />
                    </details>
                    <details className="ww-settings-section">
                      <summary>{t('wk.set.schedules')}</summary>
                      <SchedulePanel worker={selected} />
                    </details>
                    <details className="ww-settings-section" open>
                      <summary>{t('wk.set.env')}</summary>
                      <div className="workers-env-grid">
                        {selected.environments.map((environment) => (
                          <EnvironmentCard
                            key={environment.id}
                            t={t}
                            environment={environment}
                            onSave={async (env, value) => {
                              await workersApi.updateBudget(
                                selected.id,
                                env,
                                value,
                              );
                              await reload(selected.id);
                            }}
                            onRetryDomain={async (domainId) => {
                              await workersApi.retryDomain(selected.id, domainId);
                              await reload(selected.id);
                            }}
                          />
                        ))}
                      </div>
                    </details>
                  </div>
                )}
              </div></>}
            </>
          )}
        </>
      )}
    </main>
  );
};
